import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsageDataByAdvisorIdRequest } from '@/model/usageAnalyticsModel';
import { AppState } from '@/model/store';
import { selectSessionAdvisor } from '@/model/selectors';
import { aggregateMetrics, createEmptyMetrics } from '@shared/config/usageMetricsConfig';

/**
 * Hook to fetch and aggregate usage analytics data.
 * @param targetAdvisorId - Optional advisor ID override. If null/undefined, falls back to session advisor.
 */
export const useAdvisorUsageAnalytics = (targetAdvisorId?: string | null) => {
  const dispatch = useDispatch();
  const usageAnalytics = useSelector((state: AppState) => state.usageAnalytics);
  const sessionAdvisor = useSelector(selectSessionAdvisor);

  // Use target advisor ID if provided, otherwise fall back to session advisor
  const advisorId = targetAdvisorId ?? sessionAdvisor?.advisorId;

  const refetch = useCallback(() => {
    if (advisorId) {
      dispatch(fetchUsageDataByAdvisorIdRequest({ advisorId }));
    }
  }, [advisorId, dispatch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const calculatedMetrics = useMemo(() => {
    if (!usageAnalytics.data?.advisorUsageData) {
      return createEmptyMetrics();
    }
    const advisorsList = Object.values(usageAnalytics.data.advisorUsageData) as AdvisorUsageData[];
    return aggregateMetrics(advisorsList.map(a => a.usageMetrics));
  }, [usageAnalytics.data?.advisorUsageData]);

  const advisorsList = useMemo((): AdvisorUsageData[] => {
    if (!usageAnalytics.data?.advisorUsageData) {
      return [];
    }
    return Object.values(usageAnalytics.data.advisorUsageData);
  }, [usageAnalytics.data?.advisorUsageData]);

  const activeAdvisorsCount = useMemo(() => {
    return advisorsList.filter(a => a.usageMetrics.totalAdvisorLogins > 0).length;
  }, [advisorsList]);

  return {
    usageAnalytics,
    calculatedMetrics,
    advisorsList,
    activeAdvisorsCount,
    refetch,
  };
};
